import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  Shield, Users, Settings, Save, RefreshCw, Plus, Trash2, 
  Check, X, ChevronDown, ChevronRight, User, Lock, Unlock,
  LayoutDashboard, Calendar, FileText, Building2, ShoppingBag,
  CreditCard, Wallet, UserCog, UserCheck, MessageCircle, CalendarDays,
  Store, UtensilsCrossed, CalendarCheck, Truck, BookUser, PartyPopper,
  BarChart3, Share2, Image, ClipboardList, Receipt, HelpCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for modules
const MODULE_ICONS = {
  // Modules principaux
  dashboard: LayoutDashboard,
  mon_food_truck: Store,
  mon_menu: UtensilsCrossed,
  mes_services: CalendarCheck,
  mes_paiements: Wallet,
  // Gestion adhérents
  adherents: Truck,
  annuaire: BookUser,
  // Événements
  organisation_events: PartyPopper,
  planning: Calendar,
  planning_global: BarChart3,
  hec_planning: Building2,
  // Communication
  reseaux_sociaux: Share2,
  messages: MessageCircle,
  gallery: Image,
  // Finance
  bilans: BarChart3,
  crm: Users,
  sondages: ClipboardList,
  achats_bureau: ShoppingBag,
  cotisations: Receipt,
  paiements: CreditCard,
  // Administration
  utilisateurs: UserCog,
  contrats: FileText,
  validations: UserCheck,
  droits: Shield,
  parametres: Settings,
  aide: HelpCircle
};

export default function PermissionsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modules, setModules] = useState([]);
  const [permissionLevels, setPermissionLevels] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [userOverrides, setUserOverrides] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("roles"); // roles, users, levels
  const [expandedRoles, setExpandedRoles] = useState(["admin", "bureau", "membre"]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [modulesRes, configRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/permissions/modules`, { headers }),
        axios.get(`${API_URL}/api/permissions/config`, { headers }),
        axios.get(`${API_URL}/api/users`, { headers })
      ]);

      setModules(modulesRes.data);
      setPermissionLevels(configRes.data.permission_levels || []);
      setRolePermissions(configRes.data.role_permissions || {});
      setUserOverrides(configRes.data.user_overrides || []);
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Error loading permissions:", error);
      toast.error("Erreur lors du chargement des permissions");
    } finally {
      setLoading(false);
    }
  };

  const saveRolePermissions = async (role) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/permissions/role/${role}`,
        rolePermissions[role],
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Permissions du rôle "${role}" sauvegardées`);
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const updateRolePermission = (role, moduleId, levelId) => {
    setRolePermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [moduleId]: levelId
      }
    }));
  };

  const loadUserPermissions = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/permissions/user/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserPermissions(response.data);
      setSelectedUser(users.find(u => u.id === userId));
    } catch (error) {
      toast.error("Erreur lors du chargement des permissions utilisateur");
    }
  };

  const addUserOverride = async (userId, moduleId, levelId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/permissions/user-override`,
        { user_id: userId, module_id: moduleId, permission_level: levelId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Exception ajoutée");
      loadUserPermissions(userId);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout de l'exception");
    }
  };

  const removeUserOverride = async (userId, moduleId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API_URL}/api/permissions/user-override/${userId}/${moduleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Exception supprimée");
      loadUserPermissions(userId);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir réinitialiser toutes les permissions aux valeurs par défaut ?")) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/permissions/init-defaults`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Permissions réinitialisées");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  const getPermissionColor = (levelId) => {
    const level = permissionLevels.find(l => l.id === levelId);
    return level?.color || "#gray";
  };

  const getPermissionName = (levelId) => {
    const level = permissionLevels.find(l => l.id === levelId);
    return level?.name || levelId;
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: "Administrateur",
      bureau: "Bureau",
      membre: "Membre"
    };
    return labels[role] || role;
  };

  const toggleRole = (role) => {
    setExpandedRoles(prev => 
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gestion des Droits</h1>
            <p className="text-sm text-gray-500">Configurez les permissions par module</p>
          </div>
        </div>
        <Button variant="outline" onClick={resetToDefaults} className="text-red-600 hover:bg-red-50">
          <RefreshCw className="w-4 h-4 mr-2" />
          Réinitialiser
        </Button>
      </div>

      {/* Legend */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Niveaux de permission :</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {permissionLevels.map(level => (
              <Badge 
                key={level.id} 
                style={{ backgroundColor: level.color }}
                className="text-white px-3 py-1"
              >
                {level.name} (niveau {level.level})
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button 
          variant={activeTab === "roles" ? "default" : "outline"}
          onClick={() => setActiveTab("roles")}
        >
          <Users className="w-4 h-4 mr-2" />
          Par Rôle
        </Button>
        <Button 
          variant={activeTab === "users" ? "default" : "outline"}
          onClick={() => setActiveTab("users")}
        >
          <User className="w-4 h-4 mr-2" />
          Exceptions Utilisateurs
        </Button>
      </div>

      {/* Roles Tab */}
      {activeTab === "roles" && (
        <div className="space-y-4">
          {["admin", "bureau", "membre"].map(role => (
            <Card key={role} className="overflow-hidden">
              <CardHeader 
                className={`cursor-pointer transition-colors ${
                  role === "admin" ? "bg-purple-50 hover:bg-purple-100" :
                  role === "bureau" ? "bg-blue-50 hover:bg-blue-100" :
                  "bg-gray-50 hover:bg-gray-100"
                }`}
                onClick={() => toggleRole(role)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {expandedRoles.includes(role) ? 
                      <ChevronDown className="w-5 h-5" /> : 
                      <ChevronRight className="w-5 h-5" />
                    }
                    <CardTitle className="text-lg">{getRoleLabel(role)}</CardTitle>
                    <Badge variant="outline" className="ml-2">
                      {users.filter(u => u.role === role).length} utilisateurs
                    </Badge>
                  </div>
                  {expandedRoles.includes(role) && (
                    <Button 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); saveRolePermissions(role); }}
                      disabled={saving}
                    >
                      <Save className="w-4 h-4 mr-1" />
                      Sauvegarder
                    </Button>
                  )}
                </div>
              </CardHeader>
              
              {expandedRoles.includes(role) && (
                <CardContent className="p-0">
                  <div className="grid grid-cols-1 divide-y">
                    {modules.map(module => {
                      const ModuleIcon = MODULE_ICONS[module.id] || Shield;
                      const currentLevel = rolePermissions[role]?.[module.id] || "none";
                      
                      return (
                        <div 
                          key={module.id} 
                          className="flex items-center justify-between p-4 hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            <ModuleIcon className="w-5 h-5 text-gray-500" />
                            <span className="font-medium">{module.name}</span>
                          </div>
                          <div className="flex gap-2">
                            {permissionLevels.map(level => (
                              <button
                                key={level.id}
                                onClick={() => updateRolePermission(role, module.id, level.id)}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                                  currentLevel === level.id 
                                    ? "ring-2 ring-offset-2 ring-gray-400 text-white" 
                                    : "text-gray-600 hover:opacity-80"
                                }`}
                                style={{ 
                                  backgroundColor: currentLevel === level.id ? level.color : `${level.color}20`,
                                  color: currentLevel === level.id ? "white" : level.color
                                }}
                              >
                                {level.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Users Exceptions Tab */}
      {activeTab === "users" && (
        <div className="grid md:grid-cols-3 gap-6">
          {/* User List */}
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle className="text-base">Utilisateurs</CardTitle>
            </CardHeader>
            <CardContent className="p-0 max-h-[500px] overflow-y-auto">
              {users.map(u => {
                const hasOverrides = userOverrides.some(o => o.user_id === u.id);
                return (
                  <div
                    key={u.id}
                    onClick={() => loadUserPermissions(u.id)}
                    className={`p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedUser?.id === u.id ? "bg-blue-50 border-l-4 border-l-blue-500" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{u.prenom} {u.nom}</p>
                        <p className="text-xs text-gray-500">{u.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {getRoleLabel(u.role)}
                        </Badge>
                        {hasOverrides && (
                          <Badge className="bg-orange-500 text-white text-xs">
                            Exception
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* User Permissions */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                {selectedUser ? (
                  <>
                    <User className="w-5 h-5" />
                    {selectedUser.prenom} {selectedUser.nom}
                    <Badge variant="outline" className="ml-2">
                      {getRoleLabel(selectedUser.role)}
                    </Badge>
                  </>
                ) : (
                  "Sélectionnez un utilisateur"
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {userPermissions ? (
                <div className="space-y-2">
                  {modules.map(module => {
                    const ModuleIcon = MODULE_ICONS[module.id] || Shield;
                    const perm = userPermissions.permissions[module.id];
                    const hasOverride = perm?.has_override;
                    
                    return (
                      <div 
                        key={module.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          hasOverride ? "border-orange-300 bg-orange-50" : "border-gray-200"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <ModuleIcon className="w-5 h-5 text-gray-500" />
                          <span className="font-medium">{module.name}</span>
                          {hasOverride && (
                            <Badge className="bg-orange-500 text-white text-xs">
                              Exception
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={perm?.level?.id || "none"}
                            onChange={(e) => addUserOverride(selectedUser.id, module.id, e.target.value)}
                            className="text-sm border rounded-md px-2 py-1"
                            style={{ 
                              backgroundColor: `${getPermissionColor(perm?.level?.id)}20`,
                              borderColor: getPermissionColor(perm?.level?.id)
                            }}
                          >
                            {permissionLevels.map(level => (
                              <option key={level.id} value={level.id}>
                                {level.name}
                              </option>
                            ))}
                          </select>
                          {hasOverride && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:bg-red-50 h-8 w-8 p-0"
                              onClick={() => removeUserOverride(selectedUser.id, module.id)}
                              title="Supprimer l'exception"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Sélectionnez un utilisateur pour voir et modifier ses permissions</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
